import React, { useEffect, useState } from "react";
import { fetchSalespersonReport, fetchStaff, getCurrency } from "../services/api";
import { formatPrice } from "../utils/currency";
import { printTableReport } from "../utils/printDoc";
import { useT } from "../i18n/useT";

// Salesperson (seller) sales report — totals per salesperson over a date range,
// plus a per-sale list. The salesperson is who MADE the sale (defaults to the
// order-taker/agent, overridable by the cashier), distinct from the cashier who
// took the money. Manager view; basis for commission / performance.
const S = {
  en: {
    title: "Salesperson sales", subtitle: "Sales credited to each seller",
    from: "From", to: "To", salesperson: "Salesperson", all: "All salespeople",
    name: "Salesperson", sales: "Sales", count: "Bills", share: "Share",
    time: "Time", doc: "Doc", point: "Point", guest: "Customer", gross: "Amount", method: "Method",
    total: "Total", unassigned: "Unassigned", none: "No sales in this range.",
    loading: "Loading…", err: "Could not load the report.", by_person: "By salesperson", lines: "Sales",
    print: "Print",
  },
  am: {
    title: "የሻጭ ሽያጭ", subtitle: "ለእያንዳንዱ ሻጭ የተመዘገበ ሽያጭ",
    from: "ከ", to: "እስከ", salesperson: "ሻጭ", all: "ሁሉም ሻጮች",
    name: "ሻጭ", sales: "ሽያጭ", count: "ደረሰኞች", share: "ድርሻ",
    time: "ሰዓት", doc: "ሰነድ", point: "ነጥብ", guest: "ደንበኛ", gross: "መጠን", method: "ዘዴ",
    total: "ጠቅላላ", unassigned: "ያልተመደበ", none: "በዚህ ክልል ሽያጭ የለም።",
    loading: "በመጫን ላይ…", err: "ሪፖርቱን መጫን አልተቻለም።", by_person: "በሻጭ", lines: "ሽያጮች",
    print: "አትም",
  },
  om: {
    title: "Gurgurtaa gurgurtootaa", subtitle: "Gurgurtaa tokkoon tokkoon gurgurtaaf galmaa'e",
    from: "Irraa", to: "Hamma", salesperson: "Gurgurtaa", all: "Gurgurtoota hunda",
    name: "Gurgurtaa", sales: "Gurgurtaa", count: "Nagahee", share: "Qooda",
    time: "Sa'aa", doc: "Sanada", point: "Bakka", guest: "Maamila", gross: "Hanga", method: "Mala",
    total: "Ida'ama", unassigned: "Hin ramadamne", none: "Daangaa kana keessa gurgurtaan hin jiru.",
    loading: "Fe'aa jira…", err: "Gabaasa fe'uu hin dandeenye.", by_person: "Gurgurtaadhaan", lines: "Gurgurtaa",
    print: "Maxxansi",
  },
};

const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)" };
const TH = { textAlign: "left", color: "#5b6472", padding: "5px 8px", fontSize: 12.5 };
const TR = { textAlign: "right" };
const inp = { padding: 8, borderRadius: 8, border: "1px solid #d8dee2" };
const iso0 = () => new Date().toISOString().slice(0, 10);

export default function SalespersonReportPage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const cur = getCurrency() || "ETB";
  const money = (v) => formatPrice(v || 0, cur);

  const [from, setFrom] = useState(iso0());
  const [to, setTo] = useState(iso0());
  const [salespersonId, setSalespersonId] = useState("");
  const [staff, setStaff] = useState([]);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchStaff().then((r) => setStaff((r || []).filter((s) => s.active !== false))).catch(() => setStaff([])); }, []);

  const load = () => {
    setLoading(true); setErr("");
    fetchSalespersonReport({ from, to, salespersonId: salespersonId || undefined })
      .then(setData)
      .catch(() => { setErr(t("err")); setData(null); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to, salespersonId]);

  const grand = data && data.totals ? data.totals.gross : 0;

  const doPrint = () => {
    if (!data) return;
    const byPerson = {
      heading: t("by_person"),
      headers: [t("name"), t("count"), t("sales"), t("share")],
      rows: [
        ...(data.by_salesperson || []).map((r) => [
          r.salesperson_name || t("unassigned"), r.count, money(r.gross),
          grand > 0 ? `${Math.round((r.gross / grand) * 100)}%` : "—",
        ]),
        Object.assign([t("total"), data.totals.count, money(data.totals.gross), ""], { bold: true }),
      ],
    };
    const lines = {
      heading: t("lines"),
      headers: [t("time"), t("name"), t("point"), t("guest"), t("doc"), t("gross"), t("method")],
      rows: (data.lines || []).map((l) => [
        l.time || "", l.salesperson_name || t("unassigned"), l.revenue_point,
        l.guest_name || "—", l.document_number || "—", money(l.gross), l.payment_method || l.settlement_state,
      ]),
    };
    printTableReport({ title: t("title"), subtitle: `${t("from")} ${from} — ${t("to")} ${to}`, sections: [byPerson, lines] });
  };

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto" }}>
      <h1 style={{ margin: "6px 0 2px" }}>{t("title")}</h1>
      <div className="muted" style={{ fontSize: 14 }}>{t("subtitle")}</div>

      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginTop: 12 }}>
        <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("from")}{" "}
          <input type="date" style={{ ...inp, marginLeft: 4 }} value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("to")}{" "}
          <input type="date" style={{ ...inp, marginLeft: 4 }} value={to} onChange={(e) => setTo(e.target.value)} /></label>
        <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("salesperson")}{" "}
          <select style={{ ...inp, marginLeft: 4 }} value={salespersonId} onChange={(e) => setSalespersonId(e.target.value)}>
            <option value="">{t("all")}</option>
            {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        <span style={{ flex: 1 }} />
        <button className="btn secondary small" disabled={!data || (data.lines || []).length === 0} onClick={doPrint}>🖨 {t("print")}</button>
      </div>

      {err && <div className="error" style={{ marginTop: 12 }}>{err}</div>}

      {loading && !data ? (
        <p className="muted" style={{ marginTop: 16 }}>{t("loading")}</p>
      ) : data && (data.lines || []).length === 0 ? (
        <div style={{ ...CARD, padding: 16, marginTop: 14 }}><div className="muted" style={{ fontSize: 13 }}>{t("none")}</div></div>
      ) : data ? (
        <>
          {/* Totals per salesperson */}
          <div style={{ ...CARD, padding: 14, marginTop: 14, overflowX: "auto" }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>{t("by_person")}</div>
            <table style={{ width: "100%", fontSize: 13.5, borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={TH}>{t("name")}</th>
                <th style={{ ...TH, ...TR }}>{t("count")}</th>
                <th style={{ ...TH, ...TR }}>{t("sales")}</th>
                <th style={{ ...TH, ...TR }}>{t("share")}</th>
              </tr></thead>
              <tbody>
                {(data.by_salesperson || []).map((r) => (
                  <tr key={r.salesperson_staff_id || "none"}>
                    <td style={{ padding: "5px 8px", fontWeight: 600 }}>{r.salesperson_name || t("unassigned")}</td>
                    <td style={TR}>{r.count}</td>
                    <td style={TR}>{money(r.gross)}</td>
                    <td style={TR}>{grand > 0 ? `${Math.round((r.gross / grand) * 100)}%` : "—"}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: "1px solid #ddd", fontWeight: 700 }}>
                  <td style={{ padding: "6px 8px" }}>{t("total")}</td>
                  <td style={TR}>{data.totals.count}</td>
                  <td style={TR}>{money(data.totals.gross)}</td>
                  <td style={TR}></td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Per-sale list */}
          <div style={{ ...CARD, padding: 14, marginTop: 14, overflowX: "auto" }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>{t("lines")}</div>
            <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={TH}>{t("time")}</th>
                <th style={TH}>{t("name")}</th>
                <th style={TH}>{t("point")}</th>
                <th style={TH}>{t("guest")}</th>
                <th style={TH}>{t("doc")}</th>
                <th style={{ ...TH, ...TR }}>{t("gross")}</th>
                <th style={TH}>{t("method")}</th>
              </tr></thead>
              <tbody>
                {(data.lines || []).map((l, i) => (
                  <tr key={i}>
                    <td style={{ padding: "4px 8px" }}>{l.time || ""}</td>
                    <td style={{ padding: "4px 8px" }}>{l.salesperson_name || t("unassigned")}</td>
                    <td style={{ padding: "4px 8px" }}>{l.revenue_point}</td>
                    <td style={{ padding: "4px 8px" }}>{l.guest_name || "—"}</td>
                    <td style={{ padding: "4px 8px" }}>{l.document_number || "—"}</td>
                    <td style={TR}>{money(l.gross)}</td>
                    <td style={{ padding: "4px 8px" }}>{l.payment_method || l.settlement_state}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
}
